import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { ActionsMenu } from "@/pages/tina/festivals/components/page/actions-menu";
import { ApproveButton } from "@/pages/tina/festivals/components/page/approve-button";
import { DaysSection } from "@/pages/tina/festivals/components/page/days-section";
import { ExistingSection } from "@/pages/tina/festivals/components/page/existing-section";
import { FestivalSection } from "@/pages/tina/festivals/components/page/festival-section";
import { KindChangeDialog } from "@/pages/tina/festivals/components/page/kind-change-dialog";
import { KindQuestion } from "@/pages/tina/festivals/components/page/kind-question";
import { MergeDialog } from "@/pages/tina/festivals/components/page/merge-dialog";
import { OwnPageDialog } from "@/pages/tina/festivals/components/page/own-page-dialog";
import { PageHeader } from "@/pages/tina/festivals/components/page/page-header";
import { PagesSection } from "@/pages/tina/festivals/components/page/pages-section";
import { PosterField } from "@/pages/tina/festivals/components/page/poster-field";
import { PreviewBox } from "@/pages/tina/festivals/components/page/preview-box";
import { ProblemsList } from "@/pages/tina/festivals/components/page/problems-list";
import { ProgramBody } from "@/pages/tina/festivals/components/page/program-body";
import { PublishProgress } from "@/pages/tina/festivals/components/page/publish-progress";
import { RejectButton } from "@/pages/tina/festivals/components/page/reject-button";
import { SplitPagesDialog } from "@/pages/tina/festivals/components/page/split-pages-dialog";
import { SuggestionRow } from "@/pages/tina/festivals/components/page/suggestion-row";
import { UnpublishDialog } from "@/pages/tina/festivals/components/page/unpublish-dialog";
import { useFestivalPage } from "@/pages/tina/festivals/hooks/use-festival-page";
import { useFestivalRepository } from "@/pages/tina/festivals/hooks/use-festival-repository";
import { usePagePreview } from "@/pages/tina/festivals/hooks/use-page-preview";
import { dayNumbers } from "@/pages/tina/festivals/lib/page-form";
import {
  mainButton,
  shapeProblems,
  toApproveBody,
  toPublishChangesBody,
} from "@/pages/tina/festivals/lib/page-request";
import { LIST_PATH, pagePath } from "@/pages/tina/festivals/lib/paths";
import { placeSuggestion } from "@/pages/tina/festivals/lib/suggestions";

const statusOf = (error: unknown): number | undefined =>
  typeof error === "object" && error !== null
    ? (error as { statusCode?: number }).statusCode
    : undefined;

/**
 * `/tina/festivals/:id`: one festival, on one page. Keyed by the record id, so a url naming another festival (after a
 * merge, a split's "Open the new festival", a link from an event group) mounts a fresh hook and form instead of
 * rebasing one festival's edits onto another.
 */
export const FestivalPage = () => {
  const { id = "" } = useParams<{ id: string }>();
  return <FestivalPageBody key={id} recordId={id} />;
};

const unique = (lists: string[][]): string[] => [...new Set(lists.flat())];

function FestivalPageBody({ recordId }: { recordId: string }) {
  const fp = useFestivalPage(recordId);
  const { page, mode, form } = fp;

  if (fp.query.isError && !page) {
    return (
      <div className="flex flex-col items-start gap-2 p-6">
        {statusOf(fp.query.error) === 404 ? (
          <>
            <p className="text-sm font-medium">
              This festival does not exist any more.
            </p>
            <Link to={LIST_PATH} className="text-sm underline">
              Back to the festivals
            </Link>
          </>
        ) : (
          <>
            <p className="text-sm font-medium">
              The festival could not be loaded.
            </p>
            <p className="text-xs text-muted-foreground">
              {toHumanErrorMessage(fp.query.error)}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void fp.query.refetch()}
            >
              Try again
            </Button>
          </>
        )}
      </div>
    );
  }
  if (!page || !form || mode === null) {
    return (
      <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading the festival…
      </p>
    );
  }
  return (
    <LoadedFestival recordId={recordId} fp={{ ...fp, page, mode, form }} />
  );
}

type Loaded = ReturnType<typeof useFestivalPage> &
  Required<Pick<ReturnType<typeof useFestivalPage>, "page" | "mode" | "form">>;

function LoadedFestival({
  recordId,
  fp,
}: {
  recordId: string;
  fp: Loaded & {
    page: NonNullable<Loaded["page"]>;
    mode: NonNullable<Loaded["mode"]>;
    form: NonNullable<Loaded["form"]>;
  };
}) {
  const repo = useFestivalRepository();
  const { page, mode, form, dispatch, busy, run, dirty } = fp;
  const [askedKind, setAskedKind] = useState<"day_ticket" | "program" | null>(
    null,
  );
  const navigate = useNavigate();
  const [refusedAnswers, setRefusedAnswers] = useState<
    Record<string, string[]>
  >({});
  const [dialog, setDialog] = useState<
    "merge" | "split" | "own_page" | "unpublish" | null
  >(null);
  const preview = usePagePreview({ page, form, dirty, mode, busy });

  const canAskKind =
    mode === "approve" ||
    mode === "group" ||
    mode === "changes" ||
    mode === "program_live";
  const programDigest = page.skeleton_digest;
  const noConcerts = page.concerts.length === 0;
  const numbers = dayNumbers(form.days);
  const nameOf = (id: string): string =>
    page.existing.find((line) => line.mood_event_id === id)?.name ?? id;

  const shape = shapeProblems(form);
  // The page read says no poster is held: until one is typed (or an event becomes a day, which has its own), Approve
  // would be refused, so it is off before Tina is asked.
  const posterMissing =
    mode === "approve" &&
    page.blockers.includes("no_poster") &&
    form.posterUrl.trim() === "" &&
    !Object.values(form.existing).some((line) => line.choice === "use_as_day");
  const problems = unique([
    shape,
    posterMissing ? ["no_poster"] : [],
    preview.problems,
    fp.serverProblems,
  ]);
  // A problem about one day shows under that day's box alone; the list under the sections holds the rest.
  const dates = new Set(form.days.map((d) => d.day));
  const pageProblems = problems.filter(
    (problem) => !dates.has(problem.slice(problem.lastIndexOf(":") + 1)),
  );
  const wantsPoster =
    page.blockers.includes("no_poster") || problems.includes("no_poster");
  // A page that is not a day-ticket page (a program, a tour, a proposed tour that is only read) lists its concerts.
  const showsProgram =
    mode === "group" ||
    mode === "program_live" ||
    (mode === "read_only" && page.kind !== "day_ticket");
  const showsDays =
    !showsProgram &&
    (mode === "approve" || mode === "changes" || mode === "read_only");
  const editable = mode === "approve" || mode === "changes";

  const approve = () =>
    void run(() => repo.approve(recordId, toApproveBody(form, page)), "seed");

  const publishing = page.status === "publishing";
  const label = mainButton(page);
  const nothingToPublish = !page.unpublished && !dirty && !page.changes_waiting;
  const publish = () =>
    void run(
      () => repo.publishChanges(recordId, toPublishChangesBody(form, page)),
      "seed",
    );

  // Suggestions of an approved page, each in the box it is about.
  const dateNumbers = new Map(
    form.days.map((d) => [d.day, numbers.get(d.key) ?? 0] as const),
  );
  const suggestionNode = (suggestion: (typeof page.suggestions)[number]) => (
    <SuggestionRow
      key={suggestion.id}
      suggestion={suggestion}
      dayNumbers={dateNumbers}
      busy={busy}
      problems={refusedAnswers[suggestion.id]}
      onAccept={(body) => {
        setRefusedAnswers(({ [suggestion.id]: _cleared, ...rest }) => rest);
        void run(
          () => repo.acceptSuggestion(recordId, suggestion.id, body),
          "rebase",
          (answered) => answered,
          // A suggestion's 422 stays on that suggestion: it is not about the page's own body, so it must not
          // hold Publish changes back.
          (refused) =>
            setRefusedAnswers((current) => ({
              ...current,
              [suggestion.id]: refused,
            })),
        );
      }}
      onDismiss={() =>
        void run(
          () => repo.dismissSuggestion(recordId, suggestion.id),
          "rebase",
        )
      }
    />
  );
  const placed = page.suggestions.map((s) => ({
    s,
    place: placeSuggestion(s, form),
  }));
  const at = (where: string, key?: string) =>
    placed
      .filter(
        ({ place }) =>
          place.where === where &&
          (key === undefined || ("key" in place && place.key === key)),
      )
      .map(({ s }) => suggestionNode(s));
  const hasAt = (where: string) =>
    placed.some(({ place }) => place.where === where);

  const buttons = (
    <>
      <ActionsMenu
        page={page}
        mode={mode}
        busy={busy}
        onKind={() =>
          setAskedKind(page.kind === "program" ? "day_ticket" : "program")
        }
        onMerge={() => setDialog("merge")}
        onSplit={() => setDialog("split")}
        onOwnPage={() => setDialog("own_page")}
        onUnpublish={() => setDialog("unpublish")}
      />
      {mode === "approve" || mode === "group" ? (
        <RejectButton
          page={page}
          disabled={busy}
          onConfirm={() => void run(() => repo.reject(recordId), "seed")}
        />
      ) : null}
      {mode === "group" ? (
        <ApproveButton
          label="Approve group"
          disabled={
            programDigest === null || noConcerts || page.status === "publishing"
          }
          busy={busy}
          onApprove={() =>
            void run(
              () =>
                repo.approveGroup(recordId, {
                  skeleton_digest: programDigest ?? "",
                }),
              "seed",
            )
          }
        />
      ) : null}
      {mode === "approve" ? (
        <ApproveButton
          label={mainButton(page) ?? "Approve and publish"}
          // A pending preview does not hold Approve back: the route checks the same rules.
          disabled={problems.length > 0 || page.skeleton_digest === null}
          busy={busy}
          onApprove={approve}
        />
      ) : null}
      {mode === "changes" && label !== null ? (
        <ApproveButton
          label={label}
          disabled={
            publishing ||
            problems.length > 0 ||
            (label === "Publish changes" && nothingToPublish)
          }
          busy={busy}
          onApprove={publish}
        />
      ) : null}
    </>
  );

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader page={page} mode={mode}>
        {buttons}
      </PageHeader>
      {publishing && mode === "changes" ? (
        <p className="text-sm text-muted-foreground">
          Tina is publishing. This page updates by itself.
        </p>
      ) : null}
      {hasAt("top") ? (
        <div className="flex flex-col gap-2">{at("top")}</div>
      ) : null}
      {fp.notice ? (
        <div className="rounded-md border border-amber-400 bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
          <p>{fp.notice.text}</p>
          {fp.notice.dropped.length > 0 ? (
            <ul className="list-disc pl-5">
              {fp.notice.dropped.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {mode === "group" && programDigest === null ? (
        <p className="text-sm text-muted-foreground">
          Tina is rebuilding this festival. Reload in a few minutes.
        </p>
      ) : null}
      {mode === "question" ? (
        <KindQuestion
          busy={busy}
          onChoose={(kind) =>
            void run(() => repo.chooseKind(recordId, kind), "seed")
          }
        />
      ) : (
        <FestivalSection
          form={form}
          dispatch={dispatch}
          kind={page.kind}
          editable={editable}
          onAskKind={canAskKind ? setAskedKind : undefined}
          busy={busy}
        >
          {at("festival")}
        </FestivalSection>
      )}
      <PublishProgress
        page={page}
        busy={busy}
        onRetry={() =>
          void run(
            () => repo.retryPublish(recordId, { existing: [] }),
            "rebase",
          )
        }
      />
      {showsDays ? (
        <>
          <DaysSection
            form={form}
            dispatch={dispatch}
            page={page}
            problems={problems}
            editable={editable}
            suggestionsFor={(key) => at("day", key)}
            afterDays={
              hasAt("new_day") ? (
                <div className="flex flex-col gap-2">{at("new_day")}</div>
              ) : undefined
            }
          />
          <ExistingSection
            page={page}
            form={form}
            dispatch={dispatch}
            dayNumbers={numbers}
            editable={editable}
          >
            {at("existing")}
          </ExistingSection>
          <PagesSection
            page={page}
            form={form}
            dispatch={dispatch}
            editable={editable}
          />
        </>
      ) : null}
      {mode === "approve" && wantsPoster ? (
        <PosterField
          value={form.posterUrl}
          onChange={(url) => dispatch({ type: "set_poster", url })}
        />
      ) : null}
      {mode === "approve" ? (
        <>
          <ProblemsList problems={pageProblems} nameOf={nameOf} />
          <PreviewBox
            title="When you press Approve and publish, Tina makes this in Mood"
            preview={preview}
            shapeProblems={shape}
            emptyText="Nothing to make yet."
          />
        </>
      ) : null}
      {mode === "changes" ? (
        <>
          <ProblemsList problems={pageProblems} nameOf={nameOf} />
          <PreviewBox
            title={
              page.unpublished
                ? "When you press Approve and publish, Tina makes this in Mood"
                : "When you press Publish changes, Tina does this in Mood"
            }
            preview={preview}
            shapeProblems={shape}
            emptyText="Nothing to publish. Mood matches this page."
          />
        </>
      ) : null}
      {showsProgram ? (
        <ProgramBody
          page={page}
          variant={
            mode === "program_live"
              ? "live"
              : mode === "group"
                ? "group"
                : "read_only"
          }
        />
      ) : null}
      <MergeDialog
        page={page}
        dirty={dirty}
        open={dialog === "merge"}
        onOpenChange={(open) => setDialog(open ? "merge" : null)}
        onMerge={(into) =>
          void run(() => repo.mergePage(recordId, into), "seed").then(
            (outcome) => {
              if (outcome?.kind === "ok") {
                void navigate(pagePath(outcome.data.record_id));
              }
            },
          )
        }
      />
      <SplitPagesDialog
        page={page}
        dirty={dirty}
        open={dialog === "split"}
        onOpenChange={(open) => setDialog(open ? "split" : null)}
        onSplit={async (urls) => {
          const outcome = await run(
            () => repo.splitPage(recordId, urls),
            "seed",
            (result) => result.page,
          );
          return outcome?.kind === "ok" ? outcome.data.new_record_id : null;
        }}
      />
      <OwnPageDialog
        form={form}
        page={page}
        dispatch={dispatch}
        open={dialog === "own_page"}
        onOpenChange={(open) => setDialog(open ? "own_page" : null)}
      />
      <UnpublishDialog
        page={page}
        dirty={dirty}
        open={dialog === "unpublish"}
        onOpenChange={(open) => setDialog(open ? "unpublish" : null)}
        onConfirm={() => void run(() => repo.unpublish(recordId), "seed")}
      />
      <KindChangeDialog
        page={page}
        to={askedKind}
        dirty={dirty}
        onClose={() => setAskedKind(null)}
        onConfirm={(to) =>
          void run(() => repo.chooseKind(recordId, to), "seed")
        }
      />
    </div>
  );
}
